import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import RootLayout from './RootLayout';
import SuspenseComponent from '@/shared/components/Generic/SuspenseComponent/SuspenseComponent';
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/modules/auth/components/ProtectedRoute';
import { AuthGuard } from '@/modules/auth/components/AuthGuard';

const LogIn = lazy(() => import('@/modules/auth/pages/Login'));
const ForgotPassword = lazy(() => import('@/modules/auth/pages/ForgotPassword'));
const OAuthCallback = lazy(() => import('@/modules/auth/pages/OAuthCallback'));
const ResetPassword = lazy(() => import('@/modules/auth/pages/ResetPassword'));

const Dashboard = lazy(() => import('@/modules/dashboard/pages/Dashboard'));
const ArticleManagement = lazy(() => import('@/modules/articles/pages/ArticleManagement'));
const ArticleEditor = lazy(() => import('@/modules/articles/pages/ArticleEditorPage'));
const BrandManagement = lazy(() => import('@/modules/brands/pages/BrandManagement'));
const BrandEditor = lazy(() => import('@/modules/brands/pages/BrandEditorPage'));
const SupplierManagement = lazy(() => import('@/modules/suppliers/pages/SupplierManagement'));
const UserManagement = lazy(() => import('@/modules/users/pages/UserManagement'));
const UserDetailPage = lazy(() => import('@/modules/users/pages/UserDetailPage'));
const SupplierDetails = lazy(() => import('@/modules/suppliers/pages/SupplierDetails'));
const Analytics = lazy(() => import('@/modules/dashboard/pages/Analytics'));
const CategoryManagement = lazy(() => import('@/modules/categories/pages/CategoryManagement'));
const EventManagement = lazy(() => import('@/modules/events/pages/EventManagement'));
const EventEditor = lazy(() => import('@/modules/events/pages/EventEditorPage'));
const IngredientsManagement = lazy(
  () => import('@/modules/ingredients/pages/IngredientsManagement')
);
const IngredientDetails = lazy(() => import('@/modules/ingredients/pages/IngredientDetails'));
const AdvertorialManagement = lazy(
  () => import('@/modules/advertorials/pages/AdvertorialManagement')
);
const AdvertorialEditor = lazy(() => import('@/modules/advertorials/pages/AdvertorialEditorPage'));
const MediaLibrary = lazy(() => import('@/modules/media/pages/MediaLibraryPage'));
const SubscriptionManagement = lazy(
  () => import('@/modules/subscriptions/pages/SubscriptionManagement')
);
const SubscriptionDetailPage = lazy(
  () => import('@/modules/subscriptions/pages/SubscriptionDetail')
);
const EmailLogManagement = lazy(() => import('@/modules/emails/pages/EmailLogManagement'));
const SupplierScanManagement = lazy(
  () => import('@/modules/supplierScans/pages/SupplierScanManagement')
);
const SupplierScanDetails = lazy(() => import('@/modules/supplierScans/pages/SupplierScanDetails'));
const AffiliateLinksManagement = lazy(
  () => import('@/modules/affiliateLinks/pages/AffiliateLinksManagement')
);
const Profile = lazy(() => import('@/modules/profile/pages/Profile'));
const Settings = lazy(() => import('@/modules/settings/pages/Settings'));
const InsightsPage = lazy(() => import('@/modules/insights/pages/InsightsPage'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <SuspenseComponent>{children}</SuspenseComponent>;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: 'auth',
        element: (
          <AuthGuard>
            <SuspenseWrapper>
              <AuthLayout />
            </SuspenseWrapper>
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to='/auth/login' replace /> },
          { path: 'login', element: <LogIn /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'reset-password/:token', element: <ResetPassword /> },
          { path: 'oauth-callback', element: <OAuthCallback /> },
        ],
      },
      {
        path: '*',
        element: (
          <SuspenseWrapper>
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          </SuspenseWrapper>
        ),
        children: [
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'articles', element: <ArticleManagement /> },
          { path: 'articles/new', element: <ArticleEditor /> },
          { path: 'articles/:id/edit', element: <ArticleEditor /> },
          { path: 'insights', element: <InsightsPage /> },
          { path: 'brands', element: <BrandManagement /> },
          { path: 'brands/new', element: <BrandEditor /> },
          { path: 'brands/:brandId/edit', element: <BrandEditor /> },
          { path: 'users', element: <UserManagement /> },
          { path: 'users/:userId', element: <UserDetailPage /> },
          { path: 'suppliers', element: <SupplierManagement /> },
          { path: 'suppliers/new', element: <SupplierDetails /> },
          { path: 'suppliers/:id/edit', element: <SupplierDetails /> },
          { path: 'supplier-scans', element: <SupplierScanManagement /> },
          { path: 'supplier-scans/new', element: <SupplierScanDetails /> },
          { path: 'supplier-scans/:id/edit', element: <SupplierScanDetails /> },
          { path: 'categories', element: <CategoryManagement /> },
          { path: 'media', element: <MediaLibrary /> },
          { path: 'advertorials', element: <AdvertorialManagement /> },
          { path: 'advertorials/new', element: <AdvertorialEditor /> },
          { path: 'advertorials/:id/edit', element: <AdvertorialEditor /> },
          { path: 'events', element: <EventManagement /> },
          { path: 'events/new', element: <EventEditor /> },
          { path: 'events/:eventId/edit', element: <EventEditor /> },
          { path: 'ingredients', element: <IngredientsManagement /> },
          { path: 'ingredients/new', element: <IngredientDetails /> },
          { path: 'ingredients/:id/edit', element: <IngredientDetails /> },
          { path: 'analytics', element: <Analytics /> },
          { path: 'affiliate-links', element: <AffiliateLinksManagement /> },
          { path: 'subscriptions', element: <SubscriptionManagement /> },
          { path: 'subscriptions/:userId', element: <SubscriptionDetailPage /> },
          { path: 'email-logs', element: <EmailLogManagement /> },
          { path: 'settings', element: <Settings /> },
          { path: 'profile', element: <Profile /> },
        ],
      },
    ],
  },
]);
